// Shapes returned by the API (apps/api/src/services/takes.ts: TakeView, ResultView).
// Kept local so the web app depends only on the HTTP contract, not on server packages.

export type DomainCode = "STR" | "REL" | "EXE" | "INF";
export type QuadrantName = "Activated" | "Drifting" | "Caged" | "Fragmented";
export type ThemeCode = "PAST" | "PERM" | "ARM" | "EXP";
/** A triad answer (scoring v4, Part 4): the statement most like you and the one least like you, by gift name. */
export type TriadChoice = { most: string; least: string };
export type Choice = 1 | 2 | 3 | 4 | 5 | "Y" | "N" | DomainCode | TriadChoice;
/** A4/F4/YN/TF are scoring v1; F5 (frequency) and A5 (accuracy) are scoring v4. */
export type ScaleType = "A4" | "F4" | "YN" | "TF" | "F5" | "A5";

export type ChoiceOption = { value: Choice; label: string };

export type ScaledItem = {
  id: string;
  section: "freedom" | "clarity" | "gifts";
  type: ScaleType;
  /** Scoring v4 frequency items: the lead-in line above the statement ("In the recent memory, how often did you…"). */
  lead?: string;
  text: string;
};
export type DomainItem = {
  id: string;
  section: "domain";
  type: "MC4";
  prompt: string;
  options: { value: DomainCode; text: string }[];
};
/** Scoring v4, Part 4: three statements, each from a different gift. `value` is the gift name; it is never shown. */
export type TriadItem = {
  id: string;
  section: "gifts";
  type: "TRI";
  options: { value: string; text: string }[];
};
export type PublicItem = ScaledItem | DomainItem | TriadItem;
export type SectionKey = PublicItem["section"];

export interface TakeView {
  id: string;
  firstName: string;
  status: "in_progress" | "completed";
  scoringVersion: number;
  startedAt: string;
  completedAt: string | null;
  /** The paywall: "due" means checkout comes before the questions (older APIs: absent, free). */
  payment?: "none" | "due" | "paid";
  /** What checkout charges this take when it isn't the usual price (a rater's offer), in cents. */
  priceCents?: number | null;
  /** Checkout applies a code whose amount isn't known here (a personal link made with a code): no price on the button. */
  codeAtCheckout?: boolean;
  /** The event link the take came through: its name, and its discount while it's on (pre-applied at checkout). */
  event?: {
    name: string;
    pricing: "free" | "paid" | "discounted";
    discount: { percentOff: number | null; amountOffCents: number | null } | null;
  } | null;
  /** Only the scales this version uses (v1: A4 F4 YN TF; v4: F5 A5). */
  scales: Partial<Record<ScaleType, ChoiceOption[]>>;
  /** Scoring v4: the title of each of the four parts, shown above its questions. */
  parts?: Partial<Record<SectionKey, string>>;
  items: PublicItem[];
  answers: Record<string, Choice>;
  answeredCount: number;
  totalCount: number;
}

/** A complete triad answer. A half-picked one (only `most`) is not an answer. */
export const isTriadChoice = (v: unknown): v is TriadChoice =>
  typeof v === "object" &&
  v !== null &&
  typeof (v as TriadChoice).most === "string" &&
  (v as TriadChoice).most !== "" &&
  typeof (v as TriadChoice).least === "string" &&
  (v as TriadChoice).least !== "";

export interface StartTakeInput {
  firstName: string;
  email: string;
  emailConfirm: string;
  ageConfirmed: true;
  policyVersion: string;
  novosEmail?: boolean;
  source?: string;
  /** A rater's link code, when they start their own from the rater finish screen. */
  ref?: string;
  /** A campaign link slug from /go/{slug}. */
  campaign?: string;
  /** A personal link's token (/i#t=...): its price, and its email. */
  personalLink?: string;
  /** "About you": occupation is required; age bracket and gender are optional. */
  occupation?: string;
  ageBracket?: string;
  gender?: string;
  /** Optional phone number (E.164). */
  phone?: string;
  /** The URL parameters the app was opened with this visit (lib/landing.ts). */
  touch?: Record<string, string>;
  /** Ticked the texts-and-calls box, in this wording (GET /v1/config's smsConsent.version). */
  smsConsent?: true;
  smsConsentVersion?: string;
  /** A team join code (/t/{code}), with the person's agreement to share with its leaders. */
  team?: string;
  teamConsent?: true;
  /** Opt in to a scoring version other than the default (/?v=4). Unknown ones are refused with 422 unknown_version. */
  scoringVersion?: number;
}

/** GET /v1/go/{slug}: what a campaign or event link does right now. */
export interface CampaignLink {
  slug: string;
  name: string;
  phase: "open" | "pre" | "holding" | "post" | "not_started" | "closed" | "full";
  canStart: boolean;
  series: {
    name: string;
    preWindowStartsAt: string;
    preWindowEndsAt: string;
    postWindowStartsAt: string;
    postWindowEndsAt: string;
  } | null;
  /** Ready to show: what this phase means, or when the "after" opens. */
  message: string;
}

/** One side of an event's before/after pair. */
export interface SeriesSide {
  takeId: string;
  completedAt: string;
  freedom: number;
  clarity: number;
  quadrant: QuadrantName;
  primaryDomain: DomainCode;
  topGifts: string[];
  profileKey: string;
}

export interface SeriesView {
  name: string;
  phase: "pre" | "post";
  /** The other side exists but this viewer hasn't proved it's theirs (open the results email). */
  otherLocked?: boolean;
  before: SeriesSide | null;
  after: SeriesSide | null;
  /** after minus before, when both exist. */
  change: { freedom: number; clarity: number; quadrantChanged: boolean } | null;
}

export interface StartTakeResult {
  token: string;
  take: TakeView;
}

export interface SaveResponseResult {
  itemCode: string;
  value: Choice;
  answeredCount: number;
  totalCount: number;
}

export interface GiftResult {
  gift: string;
  domain: DomainCode;
  total: number;
  score: number;
  rank: number;
}

export interface OutputRecord {
  freedom: number;
  clarity: number;
  freedom_band: "strong" | "leaning";
  clarity_band: "strong" | "leaning";
  quadrant: QuadrantName;
  borderline: boolean;
  domain_counts: Record<DomainCode, number>;
  primary_domain: DomainCode;
  secondary_domain: DomainCode;
  balanced: boolean;
  expression: "free" | "bound";
  profile_key: string;
  gifts: GiftResult[];
  top_gifts: string[];
  growth_gifts: string[];
  plot: { x_pct: number; y_pct: number };
  flags: { straight_line: boolean; too_fast: boolean };
  midline?: { freedom: number; clarity: number };
  work_on: {
    theme_scores: Record<ThemeCode, number>;
    primary_theme: ThemeCode;
    secondary_theme: ThemeCode;
    mode: "work" | "guard";
    quotes: { id: string; label: string; text: string }[];
    clarity_focus: string | null;
    blind_spot: DomainCode;
  };
}

export type Theme = { code: ThemeCode; name: string; meaning: string; firstMove: string };

export interface SectionCopy {
  whyTitle: string;
  why: string;
  howTitle: string;
  how: string;
}
export interface SectionsCopy {
  freedomClarity: SectionCopy;
  domain: SectionCopy;
  gifts: SectionCopy;
}

export type CodexPartKey = "where" | "built" | "carry" | "way" | "next" | "others";
/** Addendum C4. Titles end with a period ("Where you are."). */
export type CodexPart = { key: CodexPartKey; title: string; text: string };
export interface Codex {
  title: string;
  parts: CodexPart[];
  close: string | null;
}

export interface ResultDisplay {
  profile: {
    key: string;
    name: string;
    /** Taylor's third-person write-up. */
    core: string;
    /** Second-person write-up (wording 1.1+). */
    youCore?: string | null;
  };
  domains: { code: DomainCode; name: string; line: string; count: number }[];
  /** All ten gifts in table order. `today` is the Addendum C3 line (wording 1.1+). */
  gifts: { code: string; domain: DomainCode; line: string; today?: string | null }[];
  /** Addendum C1-C3 write-ups (wording 1.1+). */
  sections?: SectionsCopy | null;
  /** tied: the two are as good as tied (within one answer), so both are named. */
  themes: { primary: Theme; secondary: Theme; tied?: boolean };
  /** Their own statements quoted back. Empty in guard mode. */
  quotes: { id: string; label: string; text: string }[];
  guardLine: string | null;
  clarityFocus: string | null;
  blindSpot: string;
  /** Addendum C4, the closing summary (wording 1.1+). */
  codex?: Codex | null;
}

/** The personalized write-up (Addendum D), as the API reports it. */
export interface WriteupView {
  /**
   * ready: show it. pending: being written, ask again in a moment. fallback: both attempts
   * failed, so the codex stands in. off: nothing has been written and nothing will be.
   */
  status: "ready" | "pending" | "fallback" | "off";
  /** The line it is shown under. */
  label: string;
  text: string | null;
  /** The text split into its headings, in the order it was written. */
  sections: { heading: string; paragraphs: string[] }[];
  /** Paragraphs written before the first heading (usually none; absent from older APIs). Shown too. */
  preamble?: string[];
  promptVersion: string | null;
  /** The thumbs already given. */
  feedback?: "up" | "down" | null;
  /** Staff without takes:read_answers: it quotes the person's own answers. */
  redacted?: boolean;
}

export interface ResultView {
  takeId: string;
  firstName: string;
  scoringVersion: number;
  wordingVersion: string;
  shownRevision: number;
  completedAt: string;
  record: OutputRecord;
  display: ResultDisplay;
  /** What others see (Addendum B). Null until the take has rater links. */
  observer?: ObserverView | null;
  /** Event before/after, when this take is part of a series. */
  series?: SeriesView | null;
  /** This person's earlier takes this viewer has proved are theirs (muted on the map). */
  history?: HistoryView | null;
  /** The personalized write-up (Addendum D). Absent on older results. */
  writeup?: WriteupView | null;
}

export interface HistoryItem {
  takeId: string;
  completedAt: string;
  freedom: number;
  clarity: number;
  quadrant: QuadrantName;
  profileKey: string;
}
export interface HistoryView {
  /** Newest first. */
  earlier: HistoryItem[];
  /** Earlier takes that exist but this viewer hasn't shown are theirs. */
  hiddenCount: number;
}

// ---------------------------------------------------------------- scoring v4 results

export type OfficePattern = "Apostle" | "Prophet" | "Evangelist" | "Shepherd" | "Teacher";

/** The stored result of a scoring v4 take (apps/api: the "v4" engine). Parts 1-2 give freedom and clarity, Part 3 the domain picks, Part 4 the nine gifts. */
export interface OutputRecordV4 {
  engine: "v4";
  freedom: number;
  clarity: number;
  quadrant: QuadrantName;
  expression: "free" | "bound";
  profile_key: string;
  plot: { x_pct: number; y_pct: number };
  /** Part 3 scenario picks per domain: 0-8, summing to 8. */
  domain_counts: Record<DomainCode, number>;
  /** The home base. */
  primary_domain: DomainCode;
  secondary_domain: DomainCode | null;
  /** All nine, ranked. `score` is 0-100; `most` is how often the gift's statement was picked as most like you. */
  gifts: { gift: string; total: number; score: number; most: number; rank: number }[];
  top_gifts: string[];
  /** Zero, one or two patterns, strongest first. */
  office_patterns: { pattern: OfficePattern; score: number }[];
  office_scores: Record<string, number>;
  /** Staff only: never shown to the person who took it. */
  flags: { managed_impression: boolean; inconsistent: boolean; straight_line: boolean; too_fast: boolean };
  /** Staff only: "lower" when any flag fired. */
  confidence: "normal" | "lower";
  work_on: {
    theme_scores: Record<ThemeCode, number>;
    primary_theme: ThemeCode;
    mode: "work" | "guard";
    quotes: { id: string; label: string; text: string }[];
    blind_spot: DomainCode;
  };
}

export interface ResultDisplayV4 {
  engine: "v4";
  profile: { key: string; name: string; core: string; youCore: string | null };
  /** STR, REL, EXE, INF with their scenario picks. */
  domains: { code: DomainCode; name: string; line: string; count: number }[];
  /** How many scenarios there were (8): the bar for a domain is count / scenarioCount. */
  scenarioCount: number;
  blindSpot: { code: DomainCode; name: string; line: string };
  /** One per gift. Lines are "" until Taylor writes them: show nothing for an empty one. */
  gifts: { code: string; line: string }[];
  /** "Scores are relative: ..." */
  giftsNote: string;
  theme: { code: ThemeCode; name: string; meaning: string; firstMove: string };
  /** Set in guard mode. */
  guardLine: string | null;
  /** Their own answers quoted back, each with its label ("In the recent memory, you said very often"). */
  quotes: { id: string; label: string; text: string }[];
  officePatterns: { pattern: string; score: number }[];
  /** "Your gifts cluster like a teacher's and an apostle's." Null when no pattern shows. */
  officeSentence: string | null;
}

export interface ResultViewV4 {
  takeId: string;
  firstName: string;
  scoringVersion: number;
  wordingVersion: string;
  shownRevision: number;
  completedAt: string;
  record: OutputRecordV4;
  display: ResultDisplayV4;
  /** What others see: the v3.1 rater test's combined view. Null when the take has no rater links. */
  observer: ObserverView | null;
  series?: SeriesView | null;
  history?: HistoryView | null;
  /** The personalized write-up (writeup-prompt v2 for v4). Absent on older results. */
  writeup?: WriteupView | null;
}

/** True for a scoring v4 result. The v1 record has no `engine`, so v1 code never has to narrow. */
export const isV4Result = (r: ResultView | ResultViewV4): r is ResultViewV4 => "engine" in r.record && r.record.engine === "v4";

// ---------------------------------------------------------------- observers (Addendum B)

export type ObserverLabel = "confirmed" | "hidden_strength" | "unexpressed";
export type ObserverStatus = "none" | "partial" | "full";

export interface ObserverRecordCounts {
  /** "mirror": the v3.1 test (v4 takes). Absent on Addendum B (v1 takes). */
  format?: "mirror";
  raters_completed: number;
  status: ObserverStatus;
  low_familiarity_raters: number;
}
export interface ObserverRecordScored extends ObserverRecordCounts {
  domain_scores: Record<DomainCode, number>;
  primary_domain: DomainCode;
  agreement: number;
  domain_match: boolean;
  gifts: { gift: string; score: number; rank: number }[];
  labels: Partial<Record<string, ObserverLabel>>;
  /** The v3.1 test: office patterns on the raters' scores (stored, not shown on their own). */
  office_patterns?: { pattern: string; score: number }[];
  /** The v3.1 test: the one of those the person's own result also shows, else null. */
  confirmed_pattern?: string | null;
}
export type ObserverRecord = ObserverRecordCounts | ObserverRecordScored;
export const hasObserverScores = (r: ObserverRecord): r is ObserverRecordScored => "domain_scores" in r;

export interface ObserverView {
  record: ObserverRecord;
  observerVersion: string;
  display: {
    suggestion: string;
    domainMismatch: string | null;
    domainMatch: string | null;
    agreement: string | null;
    statusNote: string | null;
    lowFamiliarityNote: string | null;
    labelCopy: Record<ObserverLabel, string>;
    labelOrder: ObserverLabel[];
    domainNames: Record<DomainCode, string>;
    /** The v3.1 test: an office pattern the raters see too ("The people who know you see the same pattern."). */
    confirmedPattern?: { pattern: string; line: string } | null;
    /** The v4.1 test: the raters' top two make the person's own pairing. */
    confirmedPairing?: { name: string; line: string } | null;
  };
}

/** GET /v1/takes/{takeId}/raters (409 before submit). */
export interface RaterInvite {
  slot: number;
  url: string;
  status: "open" | "started" | "completed";
}
export interface RatersView {
  invites: RaterInvite[];
  completed: number;
  /** Started from a rater link: who they described, to send one back to. */
  describeBack?: string | null;
}

/** GET /v1/rate/{code}: what a rater sees. */
interface RaterSessionBase {
  status: "open" | "started" | "completed";
  /** Someone started this link on another device (links bind to the first rater's browser). */
  inUseElsewhere?: boolean;
  name: string;
  intro: string;
  familiarity: { id: string; prompt: string; options: { value: number; label: string }[] };
  answeredCount: number;
  totalCount: number;
  finish: {
    text: string | null;
    /** Their own test; null when the event switched the offer off. */
    invite: {
      text: string;
      button: string;
      url: string;
      /** Its price (special: less than usual; 0 is free) and an event's code; null with no paywall. */
      offer: { special: boolean; priceCents: number; fullPriceCents: number; code: string | null } | null;
    } | null;
  } | null;
  /** Whether their own test will be offered when they finish (false when the event switched it off). */
  ownTest?: boolean;
}

/** Addendum B (o1): the rater test for v1 takes. */
export interface RaterSessionO1 extends RaterSessionBase {
  format: "addendum_b";
  items: { id: string; prompt: string; options: { value: DomainCode; text: string }[] }[];
  words: { id: string; prompt: string; minWords: number; maxWords: number; options: { key: string; word: string }[] };
  answers: {
    behavior: Record<string, { most: DomainCode; least: DomainCode }>;
    words: string[] | null;
    familiarity: number | null;
  };
}

/** The v3.1 test (o2), for v4 takes: six scenes (one pick), nine gift sets (most, then least), familiarity. */
export interface RaterSessionMirror extends RaterSessionBase {
  format: "mirror";
  /** The line above Part A and Part B questions (Taylor's v4.1 test), name filled in. Null on the v3.1 test. */
  instructions?: { partA: string; partB: string } | null;
  scenarios: { id: string; prompt: string; options: { value: DomainCode; text: string }[] }[];
  /** Values are statement ids ("OT1A"), never gift names. */
  sets: { id: string; options: { value: string; text: string }[] }[];
  answers: {
    scenarios: Record<string, DomainCode>;
    sets: Record<string, { most: string; least: string }>;
    familiarity: number | null;
  };
}

export type RaterSession = RaterSessionO1 | RaterSessionMirror;

export type RaterAnswerBody = { most: string; least: string } | { most: DomainCode } | { words: string[] } | { value: number };

/** GET /v1/me: everything a signed-in person's sign-in has claimed. */
export interface MyProfile {
  takeId: string;
  status: "in_progress" | "completed";
  startedAt: string;
  completedAt: string | null;
  context: { kind: "event" | "campaign" | "referral" | "direct"; label: string; phase: "pre" | "post" | null };
  quadrant: string | null;
  profileName: string | null;
  scoringVersion: number;
  answeredCount: number;
  raters: { completed: number; total: number };
}

export interface MyDashboard {
  person: { email: string; firstName: string };
  profiles: MyProfile[];
  described: { firstName: string; completedAt: string | null; how: "signed_in" | "took_own_from_link" }[];
}

/** One AI app connected to an account (GET /v1/me/connections and the staff and admin lists). */
export interface Connection {
  id: string;
  account: { kind: "staff" | "person"; email: string; name: string | null };
  client: { name: string; host: string; verified: boolean };
  scopes: string[];
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  revokedReason: "self" | "staff" | "refresh_reuse" | "code_reuse" | "sign_in_reset" | "access_disabled" | null;
}

export interface MyConnections {
  aiAccess: { enabled: boolean };
  connections: Connection[];
}

export interface ScopeInfo {
  scope: string;
  description: string;
}

/** POST /v1/oauth/consent/lookup: what the "Connect an app" page shows. */
export interface ConsentLookup {
  client: { name: string; logoUri: string | null; host: string; verified: boolean; lookalike?: boolean; redirectHost: string; loopbackOnly: boolean };
  requestedScopes: ScopeInfo[];
  accounts: {
    staff: { email: string; name: string; mfaPassed: boolean; scopes: ScopeInfo[] } | null;
    person: { email: string; firstName: string | null; mcpEnabled: boolean; leadsTeams: number; scopes: ScopeInfo[] } | null;
  };
  csrf: string;
}

/** POST /v1/personal-links/open: what a personal link fills in. */
export interface OpenedPersonalLink {
  firstName: string;
  email: string;
  phone: string | null;
  pricing: "free" | "paid" | "discounted";
  discount: { percentOff: number | null; amountOffCents: number | null } | null;
  expiresAt: string;
  /** Made by redeeming a code at /redeem: the page says it came with their code. */
  fromCode: boolean;
  /** This browser's take in progress came from this link: offer to pick it up. */
  resume: boolean;
}
